import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { Tab, Tabs } from '@mui/material';
import { useStores } from '../../../hooks/use-stores';
import EmployeesCheckboxTableImpl from '../../users/components/EmployeesCheckboxTableImpl';
import {
    CheckboxInput,
    FlexDiv,
    Heading3,
    palette,
    SearchInput,
} from '@awarego-ui/components';
import { caseAndDiacriticsInsensitiveSort } from '../../../components/table/table-helpers';
import { expandUserEmail, expandUserName } from '../../../utils/helpers';

const PAGE_SIZE = 5;

const Root = styled.div`
    width: 100%;
    max-width: 100%;
    min-width: 0;
    align-self: stretch;

    .MuiGrid-container {
        width: 100%;
        margin: 0;
    }

    .table-common.MuiTable-root {
        width: 100% !important;
        table-layout: fixed;
    }

    .MuiFormControlLabel-root {
        max-width: 100%;
        align-items: flex-start;
    }

    .MuiTableCell-root {
        overflow-wrap: anywhere;
    }

    .MuiFormControlLabel-label {
        overflow-wrap: anywhere;
        white-space: normal;
        min-width: 0;
    }
`;

function countSelected(users, selection) {
    if (!selection) return 0;
    if (selection.allSelected) {
        return users.filter((user) => !selection.unChecked[user.email]).length;
    }
    return users.filter((user) => selection.checked[user.email]).length;
}

function tabCount(selectable, selected, total) {
    if (!selectable) return `${total}`;
    return `${selected}/${total}`;
}

function makeColumns(selectable, selection) {
    return [
        {
            Header: 'Name',
            id: 'name',
            width: '50%',
            accessor: (x) => {
                return expandUserName(x.name || x.email);
            },
            sortType: caseAndDiacriticsInsensitiveSort,
            Cell: observer((x) => {
                const email = x.row.original.email;
                const label = (
                    <span style={{ overflowWrap: 'anywhere' }}>{x.value}</span>
                );
                if (!selectable) return label;
                const checked = selection.allSelected
                    ? !selection.unChecked[email]
                    : !!selection.checked[email];
                return (
                    <CheckboxInput
                        label={label}
                        checked={checked}
                        noYPadding
                        onChange={(e) => {
                            selection.setCheckedIds([email], e.target.checked);
                        }}
                    />
                );
            }),
        },
        {
            Header: 'Email',
            id: 'email',
            width: '50%',
            accessor: (x) => {
                return expandUserEmail(x.name, x.email);
            },
            Cell: (x) => (
                <span style={{ overflowWrap: 'anywhere' }}>{x.value}</span>
            ),
        },
    ];
}

function UserList({
    selectable,
    selection,
    query,
    onQuery,
    users,
    filteredUsers,
    currentPage,
    columns,
    onChangePagingParams,
    searchLabel,
}) {
    const selectedInFilter = countSelected(filteredUsers, selection);
    const allFilteredSelected =
        filteredUsers.length > 0 && selectedInFilter === filteredUsers.length;

    return (
        <FlexDiv column fullWidth gap={8} mt={16}>
            {users.length > 0 && (
                <div style={{ width: '100%' }}>
                    <SearchInput
                        fullWidth
                        placeholder="Search by name or email"
                        searchValue={query}
                        onChange={(e) => onQuery(e.target.value)}
                        onClear={() => onQuery('')}
                        inputProps={{ 'aria-label': searchLabel }}
                    />
                </div>
            )}
            {selectable && filteredUsers.length > 0 && (
                <CheckboxInput
                    label={`Select all (${filteredUsers.length})`}
                    checked={allFilteredSelected}
                    indeterminate={
                        selectedInFilter > 0 && !allFilteredSelected
                    }
                    onChange={(e) => {
                        selection.setCheckedIds(
                            filteredUsers.map((user) => user.email),
                            e.target.checked
                        );
                    }}
                />
            )}
            <EmployeesCheckboxTableImpl
                key={query}
                columns={columns}
                usersList={currentPage}
                usersTotal={filteredUsers.length}
                onChangePagingParams={onChangePagingParams}
                hidePageSizeSelector
                pageSize={PAGE_SIZE}
                loadingUsersList={false}
                rowsPerPage={PAGE_SIZE}
                NoDataComponent={
                    <Heading3>
                        {query ? 'No users match your search' : 'No users'}
                    </Heading3>
                }
            />
        </FlexDiv>
    );
}

function PreviewSyncLists({ automationId, companyId, selectable, syncStore }) {
    const { automationStore } = useStores();
    const store = syncStore || automationStore;
    const [tabOverride, setTabOverride] = useState(null);

    useEffect(() => {
        store.loadPreviewSyncLists(companyId, automationId);
        store.setPreviewUsersToAddQuery('');
        store.setPreviewUsersToRemoveQuery('');
        store.setToAddPageData(0, PAGE_SIZE);
        store.setToRemovePageData(0, PAGE_SIZE);
        return () => {
            store.setPreviewUsersToAddQuery('');
            store.setPreviewUsersToRemoveQuery('');
        };
    }, [store, companyId, automationId]);

    const { loadingPreviewSyncLists } = store;

    const onAddPage = useCallback(
        (pageIndex, pageSize) => {
            store.setToAddPageData(pageIndex, pageSize);
        },
        [store]
    );
    const onRemovePage = useCallback(
        (pageIndex, pageSize) => {
            store.setToRemovePageData(pageIndex, pageSize);
        },
        [store]
    );

    const columnsAdd = useMemo(
        () => makeColumns(selectable, store.previewUsersToAddSelection),
        [selectable, store.previewUsersToAddSelection]
    );
    const columnsRemove = useMemo(
        () => makeColumns(selectable, store.previewUsersToRemoveSelection),
        [selectable, store.previewUsersToRemoveSelection]
    );

    if (loadingPreviewSyncLists == null) return null;
    if (loadingPreviewSyncLists === true) {
        return (
            <FlexDiv px={8} py={24} alignCenter justifyCenter fullWidth>
                <Heading3>Loading...</Heading3>
            </FlexDiv>
        );
    }

    const usersToAdd = store.previewUsersToAdd;
    const usersToRemove = store.previewUsersToRemove;

    if (
        loadingPreviewSyncLists === false &&
        usersToAdd.length === 0 &&
        usersToRemove.length === 0
    ) {
        return (
            <FlexDiv px={8} py={24} alignCenter justifyCenter fullWidth>
                <Heading3>Users Lists are up to date </Heading3>
            </FlexDiv>
        );
    }

    const addSelected = countSelected(
        usersToAdd,
        store.previewUsersToAddSelection
    );
    const removeSelected = countSelected(
        usersToRemove,
        store.previewUsersToRemoveSelection
    );
    const tab =
        tabOverride ||
        (usersToAdd.length === 0 && usersToRemove.length > 0
            ? 'remove'
            : 'add');

    return (
        <Root>
            <Tabs
                value={tab}
                onChange={(_, value) => {
                    if (value === 'add') store.setToAddPageData(0, PAGE_SIZE);
                    else store.setToRemovePageData(0, PAGE_SIZE);
                    setTabOverride(value);
                }}
                sx={{
                    borderBottom: '1px solid rgba(52, 53, 52, 0.08)',
                    minHeight: 44,
                    '& .MuiTabs-indicator': {
                        backgroundColor: palette.evergreen.aspargus,
                    },
                    '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: 14,
                        minHeight: 44,
                        alignItems: 'flex-start',
                        paddingLeft: 0,
                        paddingRight: '24px',
                    },
                }}
            >
                <Tab
                    value="add"
                    label={`New Users (${tabCount(
                        selectable,
                        addSelected,
                        usersToAdd.length
                    )})`}
                />
                <Tab
                    value="remove"
                    label={`Obsolete Users (${tabCount(
                        selectable,
                        removeSelected,
                        usersToRemove.length
                    )})`}
                />
            </Tabs>
            {tab === 'add' ? (
                <UserList
                    selectable={selectable}
                    selection={store.previewUsersToAddSelection}
                    query={store.previewUsersToAddQuery}
                    onQuery={(value) => store.setPreviewUsersToAddQuery(value)}
                    users={usersToAdd}
                    filteredUsers={store.previewUsersToAddFiltered}
                    currentPage={store.previewUsersToAddCurrentPage}
                    columns={columnsAdd}
                    onChangePagingParams={onAddPage}
                    searchLabel="Search new users"
                />
            ) : (
                <UserList
                    selectable={selectable}
                    selection={store.previewUsersToRemoveSelection}
                    query={store.previewUsersToRemoveQuery}
                    onQuery={(value) =>
                        store.setPreviewUsersToRemoveQuery(value)
                    }
                    users={usersToRemove}
                    filteredUsers={store.previewUsersToRemoveFiltered}
                    currentPage={store.previewUsersToRemoveCurrentPage}
                    columns={columnsRemove}
                    onChangePagingParams={onRemovePage}
                    searchLabel="Search obsolete users"
                />
            )}
        </Root>
    );
}

export default observer(PreviewSyncLists);
